declare const adminGameSpeedMultiplierBrand: unique symbol;

export type AdminGameSpeedMultiplier = number & {
  readonly [adminGameSpeedMultiplierBrand]: "AdminGameSpeedMultiplier";
};

export const DEFAULT_ADMIN_GAME_SPEED_MULTIPLIER =
  1 as AdminGameSpeedMultiplier;
export const MAX_ADMIN_GAME_SPEED_MULTIPLIER =
  1_000_000 as AdminGameSpeedMultiplier;

export const ADMIN_GAME_SPEED_MULTIPLIER_OPTIONS: readonly AdminGameSpeedMultiplier[] =
  [1, 2, 3, 10, 100, 1000, 10_000, 1_000_000].map(
    (value) => value as AdminGameSpeedMultiplier,
  );

const MAX_RAW_FRAME_ELAPSED_SECONDS = 0.25;

const CONTINUOUS_SOFTCAP_START = 100;
const CONTINUOUS_SOFTCAP_EXPONENT = 0.5;
const CONTINUOUS_HARD_CAP = 10_000;

const TIMER_SOFTCAP_START = 25;
const TIMER_SOFTCAP_EXPONENT = 0.5;
const TIMER_HARD_CAP = 1_000;

const PURCHASE_AUTOMATION_SOFTCAP_START = 10;
const PURCHASE_AUTOMATION_SOFTCAP_EXPONENT = 0.5;
const PURCHASE_AUTOMATION_HARD_CAP = 100;

const AMPLIFIER_PROGRESSION_SOFTCAP_START = 10;
const AMPLIFIER_PROGRESSION_SOFTCAP_EXPONENT = 0.5;
const AMPLIFIER_PROGRESSION_HARD_CAP = 250;
const AMPLIFIER_UPGRADE_AUTOMATION_SOFTCAP_START = 4;
const AMPLIFIER_UPGRADE_AUTOMATION_SOFTCAP_EXPONENT = 0.5;
const AMPLIFIER_UPGRADE_AUTOMATION_HARD_CAP = 64;

const VISUAL_SPEED_HARD_CAP = 4;

const RESET_AUTOMATION_BASE_EVENT_BUDGET = 8;
const RESET_AUTOMATION_EVENT_CAP = 32;
const HGU_AUTOMATION_BASE_EVENT_BUDGET = 16;
const HGU_AUTOMATION_EVENT_CAP = 64;
const AMPLIFIER_UPGRADE_BASE_EVENT_BUDGET = 2;
const AMPLIFIER_UPGRADE_EVENT_CAP = 16;

export interface AdminGameSpeedEffectiveLane {
  readonly effectiveSpeedMultiplier: number;
  readonly isSoftCapped: boolean;
  readonly isHardCapped: boolean;
}

export interface AdminGameSpeedEventBudget {
  readonly budget: number;
  readonly cap: number;
  readonly isCapped: boolean;
}

export interface AdminGameSpeedPolicySnapshot {
  readonly adminGameSpeedMultiplier: AdminGameSpeedMultiplier;
  readonly continuous: AdminGameSpeedEffectiveLane;
  readonly timer: AdminGameSpeedEffectiveLane;
  readonly automation: {
    readonly resetEvents: AdminGameSpeedEventBudget;
    readonly hguPurchaseEvents: AdminGameSpeedEventBudget;
    readonly purchaseRepeatSpeedMultiplier: number;
    readonly isPurchaseRepeatHardCapped: boolean;
  };
  readonly amplifier: {
    readonly effectiveProgressionSpeedMultiplier: number;
    readonly isProgressionSoftCapped: boolean;
    readonly isProgressionHardCapped: boolean;
    readonly upgradeAutomationSpeedMultiplier: number;
    readonly isUpgradeAutomationHardCapped: boolean;
    readonly upgradeAutomationEventBudget: number;
    readonly upgradeAutomationEventCap: number;
  };
  readonly visual: AdminGameSpeedEffectiveLane;
}

export interface AdminGameSpeedElapsedPolicy {
  readonly rawElapsedSeconds: number;
  readonly policy: AdminGameSpeedPolicySnapshot;
  readonly continuousElapsedSeconds: number;
  readonly timerElapsedSeconds: number;
  readonly amplifierProgressionElapsedSeconds: number;
  readonly visualElapsedSeconds: number;
  readonly automation: AdminGameSpeedPolicySnapshot["automation"];
}

export interface AdminGameSpeedFramePolicy extends AdminGameSpeedElapsedPolicy {
  readonly safeRawElapsedSeconds: number;
}

export interface AdminGameSpeedPolicyController {
  get(): AdminGameSpeedMultiplier;
  set(multiplier: AdminGameSpeedMultiplier): void;
  getPolicy(): AdminGameSpeedPolicySnapshot;
  resolveFrame(rawElapsedSeconds: number): AdminGameSpeedFramePolicy;
  resolveSameSessionCatchUp(
    rawElapsedSeconds: number,
  ): AdminGameSpeedElapsedPolicy;
}

export function normalizeAdminGameSpeedMultiplier(
  value: unknown,
): AdminGameSpeedMultiplier {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    return DEFAULT_ADMIN_GAME_SPEED_MULTIPLIER;
  }
  return Math.min(
    value,
    MAX_ADMIN_GAME_SPEED_MULTIPLIER,
  ) as AdminGameSpeedMultiplier;
}

export function formatAdminGameSpeedMultiplier(value: number): string {
  const display = Number.parseFloat(value.toFixed(3));
  return `x${display}`.replace("e+", "e");
}

export function resolveAdminGameSpeedPolicy(
  adminGameSpeedMultiplier: AdminGameSpeedMultiplier,
): AdminGameSpeedPolicySnapshot {
  const continuous = resolveEffectiveLane(
    adminGameSpeedMultiplier,
    CONTINUOUS_SOFTCAP_START,
    CONTINUOUS_SOFTCAP_EXPONENT,
    CONTINUOUS_HARD_CAP,
  );
  const timer = resolveEffectiveLane(
    adminGameSpeedMultiplier,
    TIMER_SOFTCAP_START,
    TIMER_SOFTCAP_EXPONENT,
    TIMER_HARD_CAP,
  );
  const purchaseRepeat = resolveEffectiveLane(
    adminGameSpeedMultiplier,
    PURCHASE_AUTOMATION_SOFTCAP_START,
    PURCHASE_AUTOMATION_SOFTCAP_EXPONENT,
    PURCHASE_AUTOMATION_HARD_CAP,
  );
  const amplifierProgression = resolveEffectiveLane(
    adminGameSpeedMultiplier,
    AMPLIFIER_PROGRESSION_SOFTCAP_START,
    AMPLIFIER_PROGRESSION_SOFTCAP_EXPONENT,
    AMPLIFIER_PROGRESSION_HARD_CAP,
  );
  const amplifierUpgradeAutomation = resolveEffectiveLane(
    adminGameSpeedMultiplier,
    AMPLIFIER_UPGRADE_AUTOMATION_SOFTCAP_START,
    AMPLIFIER_UPGRADE_AUTOMATION_SOFTCAP_EXPONENT,
    AMPLIFIER_UPGRADE_AUTOMATION_HARD_CAP,
  );
  const visual = resolveHardCappedLane(
    adminGameSpeedMultiplier,
    VISUAL_SPEED_HARD_CAP,
  );
  const amplifierUpgradeEventBudget = resolveEventBudget(
    adminGameSpeedMultiplier,
    AMPLIFIER_UPGRADE_BASE_EVENT_BUDGET,
    AMPLIFIER_UPGRADE_EVENT_CAP,
  );

  return {
    adminGameSpeedMultiplier,
    continuous,
    timer,
    automation: {
      resetEvents: resolveEventBudget(
        adminGameSpeedMultiplier,
        RESET_AUTOMATION_BASE_EVENT_BUDGET,
        RESET_AUTOMATION_EVENT_CAP,
      ),
      hguPurchaseEvents: resolveEventBudget(
        adminGameSpeedMultiplier,
        HGU_AUTOMATION_BASE_EVENT_BUDGET,
        HGU_AUTOMATION_EVENT_CAP,
      ),
      purchaseRepeatSpeedMultiplier: purchaseRepeat.effectiveSpeedMultiplier,
      isPurchaseRepeatHardCapped: purchaseRepeat.isHardCapped,
    },
    amplifier: {
      effectiveProgressionSpeedMultiplier:
        amplifierProgression.effectiveSpeedMultiplier,
      isProgressionSoftCapped: amplifierProgression.isSoftCapped,
      isProgressionHardCapped: amplifierProgression.isHardCapped,
      upgradeAutomationSpeedMultiplier:
        amplifierUpgradeAutomation.effectiveSpeedMultiplier,
      isUpgradeAutomationHardCapped: amplifierUpgradeAutomation.isHardCapped,
      upgradeAutomationEventBudget: amplifierUpgradeEventBudget.budget,
      upgradeAutomationEventCap: amplifierUpgradeEventBudget.cap,
    },
    visual,
  };
}

export function createAdminGameSpeedPolicyController(
  initialValue: unknown = DEFAULT_ADMIN_GAME_SPEED_MULTIPLIER,
): AdminGameSpeedPolicyController {
  let multiplier = normalizeAdminGameSpeedMultiplier(initialValue);

  return {
    get(): AdminGameSpeedMultiplier {
      return multiplier;
    },
    set(nextMultiplier: AdminGameSpeedMultiplier): void {
      multiplier = normalizeAdminGameSpeedMultiplier(nextMultiplier);
    },
    getPolicy(): AdminGameSpeedPolicySnapshot {
      return resolveAdminGameSpeedPolicy(multiplier);
    },
    resolveFrame(rawElapsedSeconds: number): AdminGameSpeedFramePolicy {
      const rawElapsed = sanitizeElapsedSeconds(rawElapsedSeconds);
      const safeRawElapsedSeconds = Math.min(
        rawElapsed,
        MAX_RAW_FRAME_ELAPSED_SECONDS,
      );
      const policy = resolveAdminGameSpeedPolicy(multiplier);
      return {
        ...resolveElapsedPolicy(safeRawElapsedSeconds, policy),
        rawElapsedSeconds: rawElapsed,
        safeRawElapsedSeconds,
      };
    },
    resolveSameSessionCatchUp(
      rawElapsedSeconds: number,
    ): AdminGameSpeedElapsedPolicy {
      const rawElapsed = sanitizeElapsedSeconds(rawElapsedSeconds);
      return resolveElapsedPolicy(
        rawElapsed,
        resolveAdminGameSpeedPolicy(multiplier),
      );
    },
  };
}

function resolveElapsedPolicy(
  rawElapsedSeconds: number,
  policy: AdminGameSpeedPolicySnapshot,
): AdminGameSpeedElapsedPolicy {
  return {
    rawElapsedSeconds,
    policy,
    continuousElapsedSeconds:
      rawElapsedSeconds * policy.continuous.effectiveSpeedMultiplier,
    timerElapsedSeconds:
      rawElapsedSeconds * policy.timer.effectiveSpeedMultiplier,
    amplifierProgressionElapsedSeconds:
      rawElapsedSeconds * policy.amplifier.effectiveProgressionSpeedMultiplier,
    visualElapsedSeconds:
      rawElapsedSeconds * policy.visual.effectiveSpeedMultiplier,
    automation: policy.automation,
  };
}

function resolveEffectiveLane(
  inputMultiplier: number,
  softcapStart: number,
  exponent: number,
  hardCap: number,
): AdminGameSpeedEffectiveLane {
  const softcapped =
    inputMultiplier <= softcapStart
      ? inputMultiplier
      : softcapStart * (inputMultiplier / softcapStart) ** exponent;
  const effectiveSpeedMultiplier = Math.min(softcapped, hardCap);
  return {
    effectiveSpeedMultiplier,
    isSoftCapped: inputMultiplier > softcapStart,
    isHardCapped: effectiveSpeedMultiplier >= hardCap,
  };
}

function resolveHardCappedLane(
  inputMultiplier: number,
  hardCap: number,
): AdminGameSpeedEffectiveLane {
  const effectiveSpeedMultiplier = Math.min(inputMultiplier, hardCap);
  return {
    effectiveSpeedMultiplier,
    isSoftCapped: false,
    isHardCapped: effectiveSpeedMultiplier >= hardCap,
  };
}

function resolveEventBudget(
  inputMultiplier: number,
  baseBudget: number,
  cap: number,
): AdminGameSpeedEventBudget {
  const requestedBudget = Math.ceil(baseBudget * inputMultiplier);
  const budget = Math.min(requestedBudget, cap);
  return { budget, cap, isCapped: budget >= cap };
}

function sanitizeElapsedSeconds(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}
